import React, { useState } from "react";
import { Icon } from "../UIElements/Icon";
import { useGlobalUtils } from "../../../lib/globalUtils";
import WalletTransactionModal from "./WalletTransactionModal";
import WalletTransactionHistoryModal from "./WalletTransactionHistoryModal";

const WalletCard = ({ wallet, userId, users = [] }) => {
  const { parsePrice, parseDate } = useGlobalUtils();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const getWalletDisplayName = (type) => {
    switch (type) {
      case "cash":
        return "Cash Wallet";
      case "credit_point":
        return "Credit Points";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case "cash":
        return "DollarSign";
      case "credit_point":
        return "Star";
      default:
        return "Wallet";
    }
  };

  const getWalletColor = (type) => {
    switch (type) {
      case "cash":
        return "text-green-400";
      case "credit_point":
        return "text-yellow-400";
      default:
        return "text-blue-400";
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="rounded-lg bg-gray-700 p-3">
            <Icon
              name={getWalletIcon(wallet.type)}
              className={`h-6 w-6 ${getWalletColor(wallet.type)}`}
            />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-white">
              {getWalletDisplayName(wallet.type)}
            </h4>
            <p className="text-sm text-gray-400">
              {wallet.type === "cash" ? "Cash Balance" : "Credit Points"}
            </p>
          </div>
        </div>
        <div
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            wallet.locked
              ? "border border-red-500/20 bg-red-500/20 text-red-400"
              : "border border-green-500/20 bg-green-500/20 text-green-400"
          }`}
        >
          {wallet.locked ? "Locked" : "Active"}
        </div>
      </div>

      <div className="space-y-4">
        {/* Balance */}
        <div className="text-center">
          <p className="mb-1 text-sm text-gray-400">Current Balance</p>
          <p className={`text-3xl font-bold ${getWalletColor(wallet.type)}`}>
            {wallet.type === "credit_point"
              ? `${wallet.balance} pts`
              : parsePrice(wallet.balance)}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          {/* History Button */}
          <button
            onClick={() => setShowHistoryModal(true)}
            className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Icon name="History" className="h-4 w-4" />
            <span>History</span>
          </button>

          {/* Add/Deduct Buttons */}
          {!wallet.locked && (
            <>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
              >
                <Icon name="Plus" className="h-4 w-4" />
                <span>Add</span>
              </button>
              <button
                onClick={() => setShowDeductModal(true)}
                className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
              >
                <Icon name="Minus" className="h-4 w-4" />
                <span>Deduct</span>
              </button>
            </>
          )}
        </div>

        {/* Wallet Details */}
        <div className="space-y-3">
          {wallet.locked && wallet.locked_reason && (
            <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 p-3">
              <p className="text-sm text-red-400">
                <span className="font-medium">Lock Reason:</span>{" "}
                {wallet.locked_reason}
              </p>
              {wallet.locked_at && (
                <p className="mt-1 text-xs text-red-300">
                  Locked on: {parseDate(wallet.locked_at)}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Created:</span>
            <span className="text-sm text-white">
              {parseDate(wallet.created_at)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Last Updated:</span>
            <span className="text-sm text-white">
              {parseDate(wallet.updated_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Transaction Modals */}
      <WalletTransactionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        wallet={wallet}
        userId={userId}
        transactionType="add"
      />

      <WalletTransactionModal
        isOpen={showDeductModal}
        onClose={() => setShowDeductModal(false)}
        wallet={wallet}
        userId={userId}
        transactionType="deduct"
      />

      <WalletTransactionHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        wallet={wallet}
        userId={userId}
        users={users}
      />
    </div>
  );
};

export default WalletCard;
